/**
 * Convert DigitalOcean Spaces URLs to CDN URLs.
 * Handles all known URL formats:
 * - zipper.ams3.digitaloceanspaces.com → zipper.ams3.cdn.digitaloceanspaces.com
 * - ams3.digitaloceanspaces.com/zipper/zipper/ → zipper.ams3.cdn.digitaloceanspaces.com/zipper/
 * - ams3.cdn.digitaloceanspaces.com/zipper/zipper/ → zipper.ams3.cdn.digitaloceanspaces.com/zipper/
 */
export function toCdnUrl(url: string | undefined | null): string {
  if (!url) return ''
  let u = url.trim()
  if (!u.includes('digitaloceanspaces.com')) return u

  // Already correct CDN format
  if (u.includes('zipper.ams3.cdn.digitaloceanspaces.com')) return u

  // Strip protocol for normalization
  const proto = u.match(/^https?:\/\//) ? u.match(/^https?:\/\//)![0] : ''
  let path = proto ? u.slice(proto.length) : u

  // Fix double-bucket: ams3[.cdn].digitaloceanspaces.com/zipper/zipper/X → zipper.ams3.cdn.digitaloceanspaces.com/zipper/X
  path = path
    .replace(/^ams3\.cdn\.digitaloceanspaces\.com\/zipper\//, 'zipper.ams3.cdn.digitaloceanspaces.com/')
    .replace(/^ams3\.digitaloceanspaces\.com\/zipper\//, 'zipper.ams3.cdn.digitaloceanspaces.com/')

  // Add cdn: zipper.ams3.digitaloceanspaces.com → zipper.ams3.cdn.digitaloceanspaces.com
  if (path.includes('zipper.ams3.digitaloceanspaces.com') && !path.includes('.cdn.')) {
    path = path.replace('zipper.ams3.digitaloceanspaces.com', 'zipper.ams3.cdn.digitaloceanspaces.com')
  }

  // Ensure https
  if (!path.startsWith('http')) {
    return `https://${path}`
  }
  return `${proto}${path}`
}

const WM_API_URL = process.env.REACT_APP_API_URL

function authApiToken(): string | null {
  try {
    const raw = localStorage.getItem('kt-auth-react-v')
    return raw ? JSON.parse(raw)?.api_token || null : null
  } catch {
    return null
  }
}

function isAdminSwitchSession(token: string | null): boolean {
  if (!token) return false
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(part))?.user?.admin_switch === true
  } catch {
    return false
  }
}

/**
 * Image src for the client dashboard. For a normal client it's just the CDN url.
 * When an ADMIN is "logged in as" the client, DO-Spaces post images are routed
 * through the server-side watermark preview so the admin sees how published
 * images will look (the server applies the watermark only if it's enabled for
 * that client; otherwise it redirects to the original). Originals are untouched.
 */
export function watermarkPreviewSrc(url: string | undefined | null): string {
  const cdn = toCdnUrl(url)
  if (!cdn || !cdn.includes('digitaloceanspaces.com')) return cdn
  const token = authApiToken()
  if (!isAdminSwitchSession(token)) return cdn
  return `${WM_API_URL}/watermark-preview?url=${encodeURIComponent(cdn)}&t=${encodeURIComponent(token as string)}`
}
